import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getChannelGiblet } from '@/utils/channelGibletMap';

export interface MarketplaceItem {
  id: string;
  title: string;
  channel: string;
  creator: string;
  commercialType: "Sale" | "Lease" | "Collaborate" | "Stake" | "Follow";
  category: string;
  price: number;
  description: string;
  image?: string;
  isMinted: boolean;
  assetType: "kb" | "solution" | "gibnifty" | "channel";
  gibRating?: number;
  userTitle?: string;
  userId?: string;
  channelId?: string;
  channelSlug?: string;
  memberCount?: number;
  isPrivate?: boolean;
}

// Extracted fetch function for prefetching
export const fetchMarketplaceItems = async (channelName?: string): Promise<MarketplaceItem[]> => {
  // Run all queries in parallel for better performance
  const [kbResult, solResult, profilesResult, channelsResult, memberCountsResult] = await Promise.all([
    // Fetch minted Knowledge Bases
    channelName
      ? supabase
          .from('knowledge_bases')
          .select(`id, title, creator_id, channel_name, nft_cover_image_url, description, created_at, profiles:creator_id(display_name)`)
          .eq('status', 'published')
          .is('deleted_at', null)
          .eq('channel_name', channelName.toUpperCase())
      : supabase
          .from('knowledge_bases')
          .select(`id, title, creator_id, channel_name, nft_cover_image_url, description, created_at, profiles:creator_id(display_name)`)
          .eq('status', 'published')
          .is('deleted_at', null),
    
    // Fetch minted Solutions
    channelName
      ? supabase
          .from('solutions')
          .select(`id, title, creator_id, channel_name, nft_cover_image_url, base_price, price, category, description, commercial_type, agency, created_at, profiles:creator_id(display_name)`)
          .eq('status', 'published')
          .eq('channel_name', channelName.toUpperCase())
      : supabase
          .from('solutions')
          .select(`id, title, creator_id, channel_name, nft_cover_image_url, base_price, price, category, description, commercial_type, agency, created_at, profiles:creator_id(display_name)`)
          .eq('status', 'published'),
    
    // Fetch GibNiftys (profiles with avatars)
    supabase
      .from('profiles')
      .select(`id, display_name, title, bio, avatar_url`)
      .not('avatar_url', 'is', null)
      .not('display_name', 'is', null),
    
    // Fetch all channels
    supabase
      .from('channels')
      .select(`id, name, slug, is_private`),
    
    // Get member counts for channels
    supabase
      .from('user_organizations')
      .select('channel_id')
  ]);

  if (kbResult.error) throw kbResult.error;
  if (solResult.error) throw solResult.error;
  if (profilesResult.error) throw profilesResult.error;
  if (channelsResult.error) throw channelsResult.error;
  if (memberCountsResult.error) throw memberCountsResult.error;

  const kbData = kbResult.data || [];
  const solData = solResult.data || [];
  const profiles = profilesResult.data || [];
  const channelsData = channelsResult.data || [];
  const memberCounts = memberCountsResult.data || [];

  // Filter out profiles without avatar_url
  const validProfiles = profiles.filter(p => p.avatar_url);
  const userIds = validProfiles.map(p => p.id);

  // Fetch ratings and memberships in parallel (only if we have users)
  let ratingsMap = new Map<string, number>();
  let channelsMap = new Map<string, string[]>();

  if (userIds.length > 0) {
    const [ratingsResult, membershipsResult] = await Promise.all([
      supabase
        .from('user_gib_ratings')
        .select('user_id, overall_rating')
        .in('user_id', userIds),
      supabase
        .from('user_organizations')
        .select(`user_id, channel_id, channels:channel_id(name)`)
        .in('user_id', userIds)
    ]);

    (ratingsResult.data || []).forEach(r => {
      ratingsMap.set(r.user_id, Number(r.overall_rating) || 0);
    });

    (membershipsResult.data || []).forEach(m => {
      const userId = m.user_id;
      const chName = (m.channels as any)?.name;
      if (chName) {
        if (!channelsMap.has(userId)) {
          channelsMap.set(userId, []);
        }
        channelsMap.get(userId)!.push(chName);
      }
    });
  }

  // Count members per channel
  const memberCountMap = new Map<string, number>();
  memberCounts.forEach(m => {
    const count = memberCountMap.get(m.channel_id) || 0;
    memberCountMap.set(m.channel_id, count + 1);
  });

  // Transform Knowledge Bases to marketplace format
  const kbItems: MarketplaceItem[] = kbData.map(kb => ({
    id: kb.id,
    title: kb.title,
    channel: kb.channel_name || channelName || '',
    creator: kb.profiles?.display_name || 'Unknown',
    commercialType: 'Sale' as const,
    category: 'Knowledge Base',
    price: 0,
    description: kb.description || '',
    image: kb.nft_cover_image_url || undefined,
    isMinted: !!kb.nft_cover_image_url,
    assetType: 'kb' as const
  }));

  // Transform Solutions to marketplace format
  const solItems: MarketplaceItem[] = solData.map(sol => ({
    id: sol.id,
    title: sol.title,
    channel: sol.channel_name || channelName || '',
    creator: sol.profiles?.display_name || 'Unknown',
    commercialType: (sol.commercial_type as "Sale" | "Lease" | "Collaborate" | "Stake") || 'Sale',
    category: sol.category || 'General',
    price: Number(sol.price) || Number(sol.base_price) || 0,
    description: sol.description || '',
    image: sol.nft_cover_image_url || undefined,
    isMinted: !!sol.nft_cover_image_url,
    assetType: 'solution' as const
  }));

  // Transform GibNiftys to marketplace format - only include users with channel memberships
  const gibNiftyItems: MarketplaceItem[] = validProfiles
    .filter(profile => {
      const userChannels = channelsMap.get(profile.id) || [];
      return userChannels.length > 0;
    })
    .map(profile => {
      const userChannels = channelsMap.get(profile.id) || [];
      return {
        id: `gibnifty-${profile.id}`,
        title: profile.display_name,
        channel: userChannels.join(', '),
        creator: profile.display_name,
        commercialType: 'Follow' as const,
        category: 'GibNifty',
        price: 0,
        description: profile.bio || '',
        image: profile.avatar_url || undefined,
        isMinted: true,
        assetType: 'gibnifty' as const,
        gibRating: ratingsMap.get(profile.id) || 0,
        userTitle: profile.title || undefined,
        userId: profile.id
      };
    });

  // Transform Channels to marketplace format
  const channelItems: MarketplaceItem[] = channelsData.map(channel => {
    const memberCount = memberCountMap.get(channel.id) || 0;
    const privacyStatus = channel.is_private ? 'Private' : 'Public';
    return {
      id: `channel-${channel.id}`,
      title: channel.name,
      channel: channel.name,
      creator: 'iSPAI Platform',
      commercialType: 'Follow' as const,
      category: 'Channel',
      price: 0,
      description: `${privacyStatus} channel with ${memberCount} ${memberCount === 1 ? 'member' : 'members'}. Follow to receive updates and discover content.`,
      image: getChannelGiblet(channel.slug),
      isMinted: true,
      assetType: 'channel' as const,
      channelId: channel.id,
      channelSlug: channel.slug,
      memberCount: memberCount,
      isPrivate: channel.is_private || false
    };
  });

  // Combine all items
  return [...kbItems, ...solItems, ...gibNiftyItems, ...channelItems];
};

export const useMarketplaceItems = (channelName?: string) => {
  return useQuery({
    queryKey: ['marketplace-items', channelName],
    queryFn: () => fetchMarketplaceItems(channelName),
    staleTime: Infinity,              // Always show cached data instantly
    gcTime: 1000 * 60 * 60,           // Keep in cache for 1 hour
    refetchOnMount: true,             // Refresh in background on mount
    refetchOnWindowFocus: true,       // Refresh when user returns to tab
    placeholderData: keepPreviousData // Show previous data while fetching new
  });
};
